import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from "react";
import { Link, type LinkProps } from "react-router";
import { cn } from "./cn.ts";
import { Spinner } from "./Spinner.tsx";

type ButtonVariant = "primary" | "secondary" | "ghost" | "danger";
type ButtonSize = "sm" | "md" | "lg" | "icon";

const base = cn(
  "inline-flex shrink-0 items-center justify-center gap-2 rounded-full font-semibold leading-tight",
  "transition-colors focus-visible:outline-3 focus-visible:outline-offset-2",
  "focus-visible:outline-ring disabled:pointer-events-none disabled:opacity-55",
  "aria-disabled:pointer-events-none aria-disabled:opacity-55",
);

const variants: Record<ButtonVariant, string> = {
  primary: "bg-primary text-primary-foreground hover:bg-primary/90",
  secondary: "border border-input bg-card text-card-foreground hover:bg-muted",
  ghost: "text-current hover:bg-foreground/8",
  danger: "bg-danger text-primary-foreground hover:bg-danger/90",
};

const sizes: Record<ButtonSize, string> = {
  sm: "min-h-11 px-3.5 text-base",
  md: "min-h-11 px-5 text-lg",
  lg: "min-h-13 px-7 text-lg",
  icon: "size-11 p-0",
};

const buttonClass = (
  variant: ButtonVariant = "primary",
  size: ButtonSize = "md",
  className?: string,
): string => cn(base, variants[variant], sizes[size], className);

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  /** Shows a spinner and blocks clicks while an action runs. */
  loading?: boolean;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { variant, size, loading = false, disabled, className, type = "button", children, ...rest },
  ref,
) {
  return (
    <button
      ref={ref}
      type={type}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      className={buttonClass(variant, size, className)}
      {...rest}>
      {loading && <Spinner />}
      {children}
    </button>
  );
});

interface ButtonLinkProps extends LinkProps {
  variant?: ButtonVariant;
  size?: ButtonSize;
}

export const ButtonLink = ({ variant, size, className, ...rest }: ButtonLinkProps): ReactNode => (
  <Link className={buttonClass(variant, size, className)} {...rest} />
);
